// src/components/graphs/AverageConsumptionByCustomer.js

import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  Title,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { GRADIENT_COLORS } from 'constants/colors';
import PropTypes from 'prop-types';
import useWindowSize from 'hooks/useWindowResize';
import './ChartStyles.css';

// Register the required components
ChartJS.register(
  BarElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  Title,
  ChartDataLabels
);

const AverageConsumptionByCustomer = ({ data }) => {
  const { width } = useWindowSize();
  const isMobile = width < 768;

  // Memoize data processing
  const customerData = useMemo(() => {
    if (!data || data.length === 0) {
      return {};
    }
    return data.reduce((acc, vehicle) => {
      const customer = vehicle.customer_name || 'Unknown';
      if (!acc[customer]) {
        acc[customer] = {
          totalConsumption: 0,
          count: 0,
        };
      }
      acc[customer].totalConsumption += vehicle.average_consumption;
      acc[customer].count += 1;
      return acc;
    }, {});
  }, [data]);

  // Prepare sorted data
  const sortedData = useMemo(() => {
    return Object.keys(customerData)
      .map((key) => ({
        customer: key,
        avgConsumption:
          customerData[key].totalConsumption / customerData[key].count,
      }))
      .sort((a, b) => b.avgConsumption - a.avgConsumption);
  }, [customerData]);

  const labels = sortedData.map((item) => item.customer);
  const dataValues = sortedData.map((item) =>
    parseFloat(item.avgConsumption.toFixed(2))
  );

  // Calculate dynamic chart height
  const chartHeight = 400;

//   // Now, handle the case when there's no data to display
//   if (labels.length === 0) {
//     return (
//       <div
//         className="chart-wrapper"
//         style={{ height: chartHeight }}
//         aria-label="Average Consumption by Customer"
//       >
//         <p>No data available.</p>
//       </div>
//     );
//   }

  const chartData = useMemo(() => {
    return {
      labels,
      datasets: [
        {
          label: 'Average Consumption (Wh/km)',
          data: dataValues,
          backgroundColor: (context) => {
            const gradient = context.chart.ctx.createLinearGradient(
              0,
              0,
              0,
              context.chart.height
            );
            gradient.addColorStop(0, GRADIENT_COLORS.averageConsumption.start);
            gradient.addColorStop(1, GRADIENT_COLORS.averageConsumption.end);
            return gradient;
          },
          barThickness: 'flex',
          maxBarThickness: 50,
          borderColor: GRADIENT_COLORS.averageConsumption.border,
          borderWidth: 1,
          hoverBackgroundColor: GRADIENT_COLORS.averageConsumption.border,
        },
      ],
    };
  }, [labels, dataValues]);

  const options = useMemo(() => {
    return {
      indexAxis: 'y',
      maintainAspectRatio: false,
      responsive: true,
      scales: {
        x: {
          type: 'linear',
          title: {
            display: true,
            text: 'Average Consumption (Wh/km)',
          },
          ticks: {
            font: {
              size: isMobile ? 10 : 12,
            },
            callback: function (value) {
              return value.toLocaleString();
            },
          },
          suggestedMin: 0,
        },
        y: {
          type: 'category',
          title: {
            display: true,
            text: 'Customers',
          },
          ticks: {
            autoSkip: false,
            font: {
              size: isMobile ? 10 : 12,
            },
          },
        },
      },
      plugins: {
            datalabels: {
              display: function (context) {
                return context.dataset.data[context.dataIndex] !== 0;
              },
              color: 'black',
              align: 'end',
              anchor: 'end',
              font: {
                weight: 'bold',
                size: 12,
              },
              formatter: (value) => `${value} Wh/km`,
            },
        tooltip: {
          enabled: true,
          callbacks: {
            label: function (tooltipItem) {
              return `Average Consumption: ${tooltipItem.raw.toLocaleString()} Wh/km`;
            },
          },
          backgroundColor: GRADIENT_COLORS.averageConsumption.border,
          titleFont: {
            size: 16,
            weight: 'bold',
          },
          bodyFont: {
            size: 14,
          },
          footerFont: {
            size: 12,
            style: 'italic',
          },
        },
        legend: {
          labels: {
            font: {
              size: 14,
            },
          },
        },
      },
      hover: {
        mode: 'nearest',
        intersect: true,
      },
      animation: {
        duration: 1000,
        easing: 'easeOutBounce',
      },
    };
  }, [isMobile]);

  return (
    <div
      className="chart-wrapper"
      style={{ height: chartHeight }}
      aria-label="Average Consumption by Customer"
    >
      <Bar data={chartData} options={options} />
    </div>
  );
};

AverageConsumptionByCustomer.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      customer_name: PropTypes.string.isRequired,
      average_consumption: PropTypes.number.isRequired,
    })
  ).isRequired,
};

export default React.memo(AverageConsumptionByCustomer);
